import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthToken, OauthUser, User } from '@api/models';
import { TokenService } from '@api/services/authorization/token.service';
import { UserService } from '@api/services/user/user.service';
import { SmartyCubeStorageKeys } from '@core/config/smartycube-storage-keys';
import { Urls } from '@core/config/urls';
import { LocalStorageService, SessionStorageService, StorageKeys } from '@smartera/core';
import { Application } from '@feature/multi-dashboard/models/application';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class Oauth2Service {
	public user: OauthUser;

	private CLIENT_SECRET = '123456';
	private CLIENT_ID = 'smarty-cube';

	constructor(
		private http: HttpClient,
		private localStorageService: LocalStorageService,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
		private tokenService: TokenService,
		private router: Router
	) {
		console.log('Service - Oauth2 init.');
	}

	public login(email: string, password: string): Observable<AuthToken> {
		return this.http
			.post<AuthToken>(
				`${Urls.BASE_URL.toString()}/sec/oauth/token?${this.oAuth2RequestBody(email, password)}`,
				'',
				this.oAuthRequestOptions()
			)
			.pipe(
				map((res) => {
					this.storeAuthentication(res);
					return res;
				})
			);
	}

	public logout(): void {
		this.tokenService.close();
		this.clearAuthentication();
	}

	public readAuthenticationFromStorage(): string {
		if (localStorage.length > 2) {
			const user = this.sessionStorageService.getItem(StorageKeys.LOGGED_USER);
			const access_token = this.sessionStorageService.getItem(StorageKeys.ACCESS_TOKEN);

			if (user && access_token) {
				return user;
			}
		}
	}

	public storeAuthentication(authToken: AuthToken): void {
		this.sessionStorageService.setItem(StorageKeys.LOGGED_ORG, authToken.organizationCode);
		this.sessionStorageService.setItem(StorageKeys.ACCESS_TOKEN, authToken.access_token);
		this.sessionStorageService.setItem(StorageKeys.REFRESH_TOKEN, authToken.refresh_token);
	}

	public clearAuthentication(): void {
		this.user = null;
		this.localStorageService.clearAll();
		this.sessionStorageService.clearAll();
	}

	public fetchAndSetStorageUserInfo(): Promise<void> {
		return new Promise((resolve, reject) => {
			this.userService.getUserInfo().subscribe({
				next: (user) => {
					this.storeUserInfo(user);
					resolve();
				},
				error: (error) => {
					this.logout();
					console.log(error);
					reject();
				},
			});
		});
	}

	public storeUserInfo(user: User): void {
		this.user = new OauthUser();
		this.user = Object.assign(this.user, user);
		this.user.password = null;
		const customeDealerAndBranch = JSON.parse(this.localStorageService.getItem('branch_dealer') || null);
		if (customeDealerAndBranch) {
			this.user = Object.assign(this.user, customeDealerAndBranch);
			this.localStorageService.removeItem('branch_dealer');
		}
		this.localStorageService.setItem(StorageKeys.USER_LANGUAGE, user.language);
		this.sessionStorageService.setItem(StorageKeys.LOGGED_USER, JSON.stringify(this.user));
	}

	public handleLoginError(error: any): Observable<never> {
		let msg;

		if (error.status > 200) {
			msg = JSON.parse(error._body).error_description;
		}

		return observableThrowError(msg);
	}

	public oAuth2RequestBody(userName: string, password: string): any {
		const body =
			`username=${userName}` +
			`&password=${encodeURIComponent(password)}` +
			`&grant_type=password` +
			`&scope=read` +
			`&client_id=${this.CLIENT_ID}` +
			`&application_id=${Application.SMARTY_CUBE}`;

		// const body = {
		//   userName,
		//   password: encodeURIComponent(password),
		//   grant_type: 'password',
		//   scope: 'read',
		//   client_id: this.CLIENT_ID
		// }
		// const query = new HttpParams()
		//   .set('userName', userName)
		//   .set('password', encodeURIComponent(password))
		//   .set('grant_type', 'password')
		//   .set('scope', 'read')
		//   .set('client_id', this.CLIENT_ID)

		return body;
	}

	public oAuthRequestOptions(): object {
		const headers = new HttpHeaders()
			.append('Accept', 'application/json')
			.append('Authorization', 'Basic ' + btoa(this.CLIENT_ID + ':' + this.CLIENT_SECRET))
			.append('Content-Type', 'application/json');

		const options = { headers };
		return options;
	}
}
